/**
 * ====================================================================
 * PROPOSAL MODAL COMPONENT
 * ====================================================================
 *
 * @description
 * A specialized modal component cloned from the generic `modal.js` to serve
 * the specific layout needs of the proposal summary and breakdown views. This
 * component features a custom footer with right-aligned 'PDF' and 'WORD'
 * download buttons.
 *
 * KEY FEATURES:
 * - Custom Dialog Footer: Renders a `DialogActions` section with a flex container
 *   to align buttons to the right.
 * - Dedicated Styling: Links to its own `proposal-modal.scss` file for
 *   button colors and layout adjustments.
 * - Prop-driven Buttons: The 'PDF' and 'WORD' buttons are controlled via
 *   `onPdfClick` and `onWordClick` props.
 *
 * PROPS:
 * - All standard props from the Material-UI Dialog component are supported.
 * - open (boolean): Controls the visibility of the modal.
 * - onClose (function): Callback for closing the modal.
 * - modalHeading (string): The title displayed in the modal's header.
 * - onPdfClick (function): Callback triggered when the 'PDF' button is clicked.
 * - onWordClick (function): Callback triggered when the 'WORD' button is clicked.
 */
import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import DialogContent from '@material-ui/core/DialogContent';
import Button from 'react-bootstrap/Button'; // Using react-bootstrap button
import Icons from '../../components/icons/icons';
import './scss/proposal-modal.scss'; // A dedicated scss file for this modal

export default function ProposalModal(props) {
    const handleClose = (event, reason) => {
        // if (!props.disableBackdropClick && reason === "backdropClick") {
        //     return false;
        // }
        if (typeof props.onClose === "function") {
            props.onClose();
        }
    };
    return (
        <div className='modalContainer'>
            <Dialog
                className={`${props.primary ? 'primary' : ''} proposal-modal ${props.isVariation ? 'is-variation' : ''}`}
                onClose={handleClose}
                open={props.open || false}
                aria-labelledby='form-dialog-title'
                fullWidth={props.fullWidth}
                maxWidth={props.maxWidth}
                disableEnforceFocus
                backdropclick={props.backdropClick?.toString()}
            >
                <DialogTitle id='form-dialog-title' data-cy='modal-title'>
                    <Grid container className='titlebg'>
                        <Grid item sm={11} xs={10} className='heading'>{props.modalHeading}</Grid>
                        {!props.hideCloseIcon && <Grid item sm={1} xs={2} align='right'><Icons icon='cancel' onClick={props.onClose} data-testid="CancelIcon" /></Grid>}
                    </Grid>
                    {props.showBreakupToggle && (
                        <div className="mt-3">
                            <div className="btn-group" role="group" aria-label="Breakup view toggle">
                                <button
                                    type="button"
                                    className={`btn ${props.breakupView === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => props.onBreakupViewChange('detailed')}
                                >
                                    Detailed Breakup
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${props.breakupView === 'detailed-item' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => props.onBreakupViewChange('detailed-item')}
                                >
                                    Detailed Item Breakup
                                </button>
                            </div>
                        </div>
                    )}
                </DialogTitle>
                <DialogContent onScroll={props.onScroll} className='modal-body' data-cy='modal-content'>
                    <div className='title'>{props.modalTitle}</div>
                    <div className='content'>{props.modalContent || props.children}</div>
                </DialogContent>
                <DialogActions className='proposal-dialog-actions' data-cy='modal-actions'>
                    <div className='d-flex justify-content-end w-100'>
                        <Button className="proposal-pdf-btn" onClick={props.onPdfClick}>PDF</Button>
                        <Button className="proposal-word-btn" onClick={props.onWordClick}>WORD</Button>
                    </div>
                </DialogActions>
            </Dialog>
        </div>
    )
} 